import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { form, FormField as SignalFormField, required } from '@angular/forms/signals';
import {
  AppSvgIconComponent,
  SignalCheckbox,
  SignalNumberField,
  SignalTextField,
} from '@vallift/ngl';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import type { BoundValue, FormField } from '../../../../models/built-form';
import {
  formatDataLibraryValue,
  resolveLinkedVariable,
} from '../../../../utils/resolve-linked-variable';
import { LinkVariableButtonComponent } from '../../../link-variable-button/link-variable-button';
import type { VariablePickerResult } from '../../../variable-picker-overlay/variable-picker-overlay';

interface TextConfigModel {
  label: string;
  placeholder: string;
  helpText: string;
  defaultValue: string;
  required: boolean;
  minLength: number | null;
  maxLength: number | null;
  pattern: string;
  customMessage: string;
}

@Component({
  selector: 'app-text-config-view',
  standalone: true,
  imports: [
    AppSvgIconComponent,
    LinkVariableButtonComponent,
    SignalFormField,
    SignalTextField,
    SignalNumberField,
    SignalCheckbox,
  ],
  templateUrl: './text-config-view.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TextConfigViewComponent {
  private readonly userAPIService = inject(WorkspaceAPIService);
  private readonly destroyRef = inject(DestroyRef);

  readonly field = input.required<FormField>();
  readonly fieldChange = output<FormField>();

  readonly model = signal<TextConfigModel>({
    label: '',
    placeholder: '',
    helpText: '',
    defaultValue: '',
    required: false,
    minLength: null,
    maxLength: null,
    pattern: '',
    customMessage: '',
  });
  readonly linkedVariable = signal<VariablePickerResult | null>(null);

  readonly isVariableLinked = computed(() => this.linkedVariable() != null);

  readonly configForm = form(this.model, (schema) => {
    required(schema.label);
  });

  private lastFieldId: string | null = null;
  private suppressEmit = false;

  constructor() {
    effect(() => {
      const next = this.field();
      if (next.id === this.lastFieldId) {
        return;
      }
      this.lastFieldId = next.id;
      this.suppressEmit = true;
      const validation = next.validation ?? {};
      const bound = next.default;
      if (bound?.source === 'variable') {
        const linked: VariablePickerResult = {
          id: bound.dataLibraryId,
          name: bound.label ?? bound.key ?? 'Variable',
          key: bound.key ?? bound.dataLibraryId,
          valueType: 'text',
          value: bound.value ?? null,
          valueLabel: formatDataLibraryValue(bound.value),
          source: 'dataLibrary',
        };
        this.linkedVariable.set(linked);
        if (bound.value == null) {
          this.hydrateLinkedVariable(linked.id, 'text');
        }
      } else if (bound?.source === 'system') {
        this.linkedVariable.set({
          id: bound.systemKey,
          name: bound.label ?? bound.key ?? bound.systemKey,
          key: bound.key ?? bound.systemKey,
          valueType: 'text',
          value: bound.value ?? null,
          valueLabel:
            formatDataLibraryValue(bound.value) !== '—'
              ? formatDataLibraryValue(bound.value)
              : (bound.key ?? bound.systemKey),
          source: 'system',
          systemKey: bound.systemKey,
        });
      } else {
        this.linkedVariable.set(null);
      }
      this.model.set({
        label: next.label,
        placeholder: next.placeholder ?? '',
        helpText: next.helpText ?? '',
        defaultValue:
          bound?.source === 'static' && typeof bound.value === 'string'
            ? bound.value
            : typeof next.config?.defaultValue === 'string'
              ? next.config.defaultValue
              : '',
        required: next.required ?? validation.required ?? false,
        minLength: validation.minLength ?? null,
        maxLength: validation.maxLength ?? null,
        pattern: validation.pattern ?? '',
        customMessage: validation.customMessage ?? '',
      });
    });

    effect(() => {
      const draft = this.model();
      const linked = this.linkedVariable();

      if (this.suppressEmit) {
        this.suppressEmit = false;
        return;
      }
      const field = untracked(() => this.field());

      if (!field || field.id !== this.lastFieldId) {
        return;
      }

      const nextDefault = this.toBoundDefault(draft.defaultValue, linked);
      const { default: _omitDefault, config: prevConfig, ...rest } = field;
      const nextConfig = { ...(prevConfig ?? {}) };
      if (
        nextDefault?.source === 'static' &&
        typeof nextDefault.value === 'string'
      ) {
        nextConfig.defaultValue = nextDefault.value;
      } else {
        delete (nextConfig as { defaultValue?: string }).defaultValue;
      }

      this.fieldChange.emit({
        ...rest,
        label: draft.label.trim(),
        placeholder: draft.placeholder.trim() || undefined,
        helpText: draft.helpText.trim() || undefined,
        required: draft.required,
        ...(nextDefault ? { default: nextDefault } : {}),
        config: Object.keys(nextConfig).length ? nextConfig : undefined,
        validation: {
          required: draft.required,
          minLength: draft.minLength ?? undefined,
          maxLength: draft.maxLength ?? undefined,
          pattern: draft.pattern.trim() || undefined,
          customMessage: draft.customMessage.trim() || undefined,
        },
        options: undefined,
        headingLevel: undefined,
        spacerSize: undefined,
      });
    });
  }

  onVariableSelected(result: VariablePickerResult): void {
    this.suppressEmit = false;
    this.linkedVariable.set(result);
    this.model.update((current) => ({ ...current, defaultValue: '' }));
  }

  detachLinkedVariable(): void {
    const linked = this.linkedVariable();
    const raw = linked?.value;
    const staticValue =
      typeof raw === 'string'
        ? raw
        : raw != null
          ? String(raw)
          : '';
    this.linkedVariable.set(null);
    this.model.update((current) => ({
      ...current,
      defaultValue: staticValue,
    }));
  }

  removeLinkedVariable(): void {
    this.linkedVariable.set(null);
    this.model.update((current) => ({
      ...current,
      defaultValue: '',
    }));
  }

  private hydrateLinkedVariable(
    dataLibraryId: string,
    fallbackValueType: VariablePickerResult['valueType'],
  ): void {
    resolveLinkedVariable(this.userAPIService, dataLibraryId, fallbackValueType)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((resolved) => {
        if (!resolved || this.linkedVariable()?.id !== dataLibraryId) {
          return;
        }
        this.suppressEmit = true;
        this.linkedVariable.set(resolved);
      });
  }

  private toBoundDefault(
    staticValue: string,
    linked: VariablePickerResult | null,
  ): BoundValue | undefined {
    if (linked?.source === 'system' || linked?.systemKey) {
      const systemKey = linked.systemKey ?? linked.key;
      return {
        source: 'system',
        systemKey,
        label: linked.name,
        key: linked.key,
        value: linked.value ?? null,
      };
    }
    if (linked) {
      return {
        source: 'variable',
        dataLibraryId: linked.id,
        label: linked.name,
        key: linked.key,
        value: linked.value ?? null,
      };
    }
    const trimmed = staticValue.trim();
    if (!trimmed) {
      return undefined;
    }
    return { source: 'static', value: trimmed };
  }
}
